var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import Big from "big.js";
import { currencyIdToMonetaryCurrency, decodeFixedPointType, decodePermill, newCurrencyId, newMonetaryAmount, storageKeyToNthInner, calculateAnnualizedRewardAmount, adjustToThreshold, calculateBorrowLimit, getTotalAmountBtc, calculateLtv, calculateThreshold, calculateBorrowLimitBtcChangeFactory, calculateLtvAndThresholdsChangeFactory, newAccountId, LOANS_REWARD_INDEX_SCALING_FACTOR, } from "../utils";
export class DefaultLoansAPI {
    constructor(api, wrappedCurrency, oracleAPI) {
        this.api = api;
        this.wrappedCurrency = wrappedCurrency;
        this.oracleAPI = oracleAPI;
    }
    getLoansMarkets() {
        return __awaiter(this, void 0, void 0, function* () {
            const entries = (yield this.api.query.loans.markets.entries()).filter((entry) => entry[1].isSome);
            const parsedMarkets = yield Promise.all(entries.map(([key, market]) => __awaiter(this, void 0, void 0, function* () {
                const underlyingCurrencyId = storageKeyToNthInner(key);
                const underlyingCurrency = yield currencyIdToMonetaryCurrency(this.api, underlyingCurrencyId);
                return [underlyingCurrency, market.unwrap()];
            })));
            return parsedMarkets;
        });
    }
    static getLendTokenFromUnderlyingCurrency(currency, lendTokenId) {
        return {
            name: `q${currency.name}`,
            ticker: `q${currency.ticker}`,
            decimals: currency.decimals,
            lendToken: {
                id: lendTokenId.asLendToken.toNumber(),
            },
        };
    }
    getLendTokenIdFromUnderlyingCurrency(currency) {
        return __awaiter(this, void 0, void 0, function* () {
            const currencyId = newCurrencyId(this.api, currency);
            const { value } = yield this.api.query.loans.markets(currencyId);
            return value.lendTokenId;
        });
    }
    convertLendTokenToUnderlyingCurrency(amount, underlyingCurrencyId) {
        return __awaiter(this, void 0, void 0, function* () {
            const exchangeRate = yield this.api.query.loans.exchangeRate(underlyingCurrencyId);
            const decodedExchangeRate = decodeFixedPointType(exchangeRate);
            return amount.mul(decodedExchangeRate);
        });
    }
    /**
     * Get lend position amounts in both underlying and lend currencies.
     *
     * @param accountId AccountId to get position information about
     * @param lendTokenId LendToken CurrencyId of the position
     * @param underlyingCurrencyId Underlying CurrencyId of the position
     * @returns Lend position amounts in underlying currency and lend token
     */
    getLendPositionAmounts(accountId, lendTokenId, underlyingCurrencyId) {
        return __awaiter(this, void 0, void 0, function* () {
            const lendTokenBalance = yield this.api.query.tokens.accounts(accountId, lendTokenId);
            const lendTokenBalanceTotal = lendTokenBalance.free.add(lendTokenBalance.reserved);
            const lendTokenBalanceInBig = Big(lendTokenBalanceTotal.toString());
            const amountInUnderlying = yield this.convertLendTokenToUnderlyingCurrency(lendTokenBalanceInBig, underlyingCurrencyId);
            const reservedAmountInUnderlying = yield this.convertLendTokenToUnderlyingCurrency(Big(lendTokenBalance.reserved.toString()), underlyingCurrencyId);
            return [amountInUnderlying, reservedAmountInUnderlying];
        });
    }
    getLendTokens() {
        return __awaiter(this, void 0, void 0, function* () {
            const marketEntries = yield this.getLoansMarkets();
            return marketEntries.map(([currency, loansMarket]) => DefaultLoansAPI.getLendTokenFromUnderlyingCurrency(currency, loansMarket.lendTokenId));
        });
    }
    getLendTokenExchangeRates() {
        return __awaiter(this, void 0, void 0, function* () {
            const exchangeRateEntries = yield this.api.query.loans.exchangeRate.entries();
            const underlyingCurrencies = yield Promise.all(exchangeRateEntries.map(([key]) => currencyIdToMonetaryCurrency(this.api, storageKeyToNthInner(key))));
            return exchangeRateEntries.reduce((exchangeRates, [, rate], index) => {
                const underlyingCurrencyTicker = underlyingCurrencies[index].ticker;
                return Object.assign(Object.assign({}, exchangeRates), { [underlyingCurrencyTicker]: decodeFixedPointType(rate) });
            }, {});
        });
    }
    getBorrowerAccountIds() {
        return __awaiter(this, void 0, void 0, function* () {
            const accountBorrows = yield this.api.query.loans.accountBorrows.entries();
            return [
                // Even if two `AccountId`s store the same ID, the actual objects will not be equal when compared,
                // so need to use the string representation
                ...new Set(accountBorrows.map((key) => storageKeyToNthInner(key[0], 1).toString())),
            ].map((accountIdString, _index, _arr) => newAccountId(this.api, accountIdString));
        });
    }
    getUndercollateralizedBorrowers() {
        return __awaiter(this, void 0, void 0, function* () {
            const borrowers = yield this.getBorrowerAccountIds();
            const [liquidity, borrows, collateral] = yield Promise.all([
                Promise.all(borrowers.map(this.getLiquidationThresholdLiquidity.bind(this))),
                Promise.all(borrowers.map(this.getBorrowPositionsOfAccount.bind(this))),
                Promise.all(borrowers.map(this.getLendPositionsOfAccount.bind(this))),
            ]);
            const undercollateralizedPositions = [];
            for (let i = 0; i < borrowers.length; i++) {
                undercollateralizedPositions.push({
                    accountId: borrowers[i],
                    shortfall: liquidity[i].shortfall,
                    collateralPositions: collateral[i].filter((position) => position.isCollateral),
                    borrowPositions: borrows[i],
                });
            }
            return undercollateralizedPositions.filter((position) => !position.shortfall.isZero());
        });
    }
    getLiquidationThresholdLiquidity(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            const [rawLiquidity, rawShortfall] = yield this.api.rpc.loans.getLiquidationThresholdLiquidity(accountId);
            return {
                liquidity: newMonetaryAmount(decodeFixedPointType(rawLiquidity), this.wrappedCurrency, false),
                shortfall: newMonetaryAmount(decodeFixedPointType(rawShortfall), this.wrappedCurrency, false),
            };
        });
    }
    _getLendPosition(accountId, underlyingCurrency, lendTokenId) {
        return __awaiter(this, void 0, void 0, function* () {
            const [underlyingCurrencyAmount, reservedAmountInUnderlyingCurrency] = yield this.getLendPositionAmounts(accountId, lendTokenId, newCurrencyId(this.api, underlyingCurrency));
            // Returns null if position does not exist
            if (underlyingCurrencyAmount.eq(0)) {
                return null;
            }
            const accountDeposits = yield this.api.query.loans.accountDeposits(lendTokenId, accountId);
            const isCollateral = !accountDeposits.isZero();
            // MEMO: If position is not used as loan collateral, but has reserved amount it means
            // this account uses the qToken as vault collateral.
            const amountLockedAsVaultCollateral = isCollateral ? Big(0) : reservedAmountInUnderlyingCurrency;
            return {
                amount: newMonetaryAmount(underlyingCurrencyAmount, underlyingCurrency),
                isCollateral,
                vaultCollateralAmount: newMonetaryAmount(amountLockedAsVaultCollateral, underlyingCurrency),
            };
        });
    }
    _calculateAccumulatedDebt(borrowedAmount, snapshotBorrowIndex, currentBorrowIndex) {
        if (snapshotBorrowIndex.eq(0)) {
            return Big(0);
        }
        // @note Formula for computing total debt: https://docs.parallel.fi/parallel-finance/#2.6-interest-rate-index
        // To compute only earned debt, subtract 1 from factor
        const factor = currentBorrowIndex.div(snapshotBorrowIndex).sub(1);
        return borrowedAmount.mul(factor).round(0, 3 /* RoundingMode.RoundUp */);
    }
    _getBorrowPosition(accountId, underlyingCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            const underlyingCurrencyPrimitive = newCurrencyId(this.api, underlyingCurrency);
            const [borrowSnapshot, marketStatus] = yield Promise.all([
                this.api.query.loans.accountBorrows(underlyingCurrencyPrimitive, accountId),
                this.api.rpc.loans.getMarketStatus(underlyingCurrencyPrimitive),
            ]);
            const borrowedAmount = Big(borrowSnapshot.principal.toString());
            if (borrowedAmount.eq(0)) {
                return null;
            }
            const snapshotBorrowIndex = Big(decodeFixedPointType(borrowSnapshot.borrowIndex));
            const currentBorrowIndex = Big(decodeFixedPointType(marketStatus[6]));
            const accumulatedDebt = this._calculateAccumulatedDebt(borrowedAmount, snapshotBorrowIndex, currentBorrowIndex);
            return {
                amount: newMonetaryAmount(borrowedAmount, underlyingCurrency),
                accumulatedDebt: newMonetaryAmount(accumulatedDebt, underlyingCurrency),
            };
        });
    }
    _getPositionsOfAccount(accountId, getSinglePosition) {
        return __awaiter(this, void 0, void 0, function* () {
            const marketsEntries = yield this.getLoansMarkets();
            return (yield Promise.all(marketsEntries.map(([currency, loansMarket]) => {
                return getSinglePosition(accountId, currency, loansMarket.lendTokenId);
            }))).filter((position) => position !== null);
        });
    }
    getLendPositionsOfAccount(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._getPositionsOfAccount(accountId, this._getLendPosition.bind(this));
        });
    }
    getBorrowPositionsOfAccount(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this._getPositionsOfAccount(accountId, this._getBorrowPosition.bind(this));
        });
    }
    _checkLoanAssetDataAvailability(positions, loanAssets) {
        for (const position of positions) {
            if (loanAssets[position.amount.currency.ticker] === undefined) {
                throw new Error(`No loan asset data found for currency ${position.amount.currency.name}.`);
            }
        }
    }
    getLendingStats(lendPositions, borrowPositions, loanAssets) {
        this._checkLoanAssetDataAvailability([...lendPositions, ...borrowPositions], loanAssets);
        const lendCollateralPositions = lendPositions.filter(({ isCollateral }) => isCollateral);
        const lendCollateralThresholdAdjustedPositions = lendCollateralPositions.map((position) => {
            const collateralTheshold = loanAssets[position.amount.currency.ticker].collateralThreshold;
            return Object.assign(Object.assign({}, position), { amount: adjustToThreshold(position.amount, collateralTheshold) });
        });
        const lendLiquidationThresholdAdjustedPositions = lendCollateralPositions.map((position) => {
            const liquidationThreshold = loanAssets[position.amount.currency.ticker].liquidationThreshold;
            return Object.assign(Object.assign({}, position), { amount: adjustToThreshold(position.amount, liquidationThreshold) });
        });
        const borrowPositionsWithDebt = borrowPositions.map((_a) => {
            var { amount, accumulatedDebt } = _a, rest = __rest(_a, ["amount", "accumulatedDebt"]);
            return (Object.assign(Object.assign({}, rest), { accumulatedDebt, amount: amount.add(accumulatedDebt) }));
        });
        const totalLentBtc = getTotalAmountBtc(lendPositions, loanAssets);
        const totalBorrowedBtc = getTotalAmountBtc(borrowPositionsWithDebt, loanAssets);
        const totalCollateralBtc = getTotalAmountBtc(lendCollateralPositions, loanAssets);
        const totalCollateralThresholdAdjustedCollateralBtc = getTotalAmountBtc(lendCollateralThresholdAdjustedPositions, loanAssets);
        const totalLiquidationThresholdAdjustedCollateralBtc = getTotalAmountBtc(lendLiquidationThresholdAdjustedPositions, loanAssets);
        const borrowLimitBtc = calculateBorrowLimit(totalBorrowedBtc, totalCollateralThresholdAdjustedCollateralBtc);
        const ltv = calculateLtv(totalCollateralBtc, totalBorrowedBtc);
        const collateralThresholdWeightedAverage = calculateThreshold(totalCollateralBtc, totalCollateralThresholdAdjustedCollateralBtc);
        const liquidationThresholdWeightedAverage = calculateThreshold(totalCollateralBtc, totalLiquidationThresholdAdjustedCollateralBtc);
        const calculateBorrowLimitBtcChange = calculateBorrowLimitBtcChangeFactory(loanAssets, totalBorrowedBtc, totalCollateralThresholdAdjustedCollateralBtc);
        const calculateLtvAndThresholdsChange = calculateLtvAndThresholdsChangeFactory(loanAssets, totalBorrowedBtc, totalCollateralBtc, totalCollateralThresholdAdjustedCollateralBtc, totalLiquidationThresholdAdjustedCollateralBtc);
        return {
            totalBorrowedBtc,
            totalCollateralBtc,
            totalLentBtc,
            borrowLimitBtc,
            ltv,
            liquidationThresholdWeightedAverage,
            collateralThresholdWeightedAverage,
            calculateBorrowLimitBtcChange,
            calculateLtvAndThresholdsChange,
        };
    }
    _getLendApy(underlyingCurrencyId) {
        return __awaiter(this, void 0, void 0, function* () {
            const rawLendApy = yield this.api.query.loans.supplyRate(underlyingCurrencyId);
            // Return percentage
            return decodeFixedPointType(rawLendApy).mul(100);
        });
    }
    _getBorrowApy(underlyingCurrencyId) {
        return __awaiter(this, void 0, void 0, function* () {
            const rawBorrowApy = yield this.api.query.loans.borrowRate(underlyingCurrencyId);
            // Return percentage
            return decodeFixedPointType(rawBorrowApy).mul(100);
        });
    }
    _getTotalLiquidityCapacityAndBorrows(underlyingCurrency, underlyingCurrencyId) {
        return __awaiter(this, void 0, void 0, function* () {
            const lendTokenId = yield this.getLendTokenIdFromUnderlyingCurrency(underlyingCurrency);
            const [lendTokenTotalIssuance, totalBorrows, exchangeRate] = yield Promise.all([
                this.api.query.tokens.totalIssuance(lendTokenId),
                this.api.query.loans.totalBorrows(underlyingCurrencyId),
                this.api.query.loans.exchangeRate(underlyingCurrencyId),
            ]);
            const [totalLiquidity, availableCapacity] = this._calculateLiquidityAndCapacityAmounts(underlyingCurrency, Big(lendTokenTotalIssuance.toString()), Big(totalBorrows.toString()), decodeFixedPointType(exchangeRate));
            const totalBorrowsMonetary = newMonetaryAmount(totalBorrows.toString(), underlyingCurrency);
            return [totalLiquidity, availableCapacity, totalBorrowsMonetary];
        });
    }
    _calculateLiquidityAndCapacityAmounts(underlyingCurrency, lendTokenTotalIssuance, totalBorrows, exchangeRate) {
        const totalLiquidity = lendTokenTotalIssuance.mul(exchangeRate);
        // @note Available capacity to borrow is being computed in a different way
        // than in the runtime: https://docs.parallel.fi/parallel-finance/#2.1-internal-exchange-rate
        const availableCapacity = totalLiquidity.sub(totalBorrows);
        const totalLiquidityMonetary = newMonetaryAmount(totalLiquidity, underlyingCurrency);
        const availableCapacityMonetary = newMonetaryAmount(availableCapacity, underlyingCurrency);
        return [totalLiquidityMonetary, availableCapacityMonetary];
    }
    /**
     * Get the lend and borrow annual rewards for 1 UNIT of a given underlying currency id.
     *
     * @param underlyingCurrencyId currency id to get reward amounts for.
     * @returns Annualized lend and borrow rewards for 1 unit of the given underlying currency.
     */
    _getLendAndBorrowYearlyRewardAmount(underlyingCurrencyId, totalLiquidity, totalBorrows) {
        return __awaiter(this, void 0, void 0, function* () {
            const blockTimeMs = (yield this.api.call.auraApi.slotDuration()).toNumber();
            const [lendRewardPerPool, borrowRewardPerPool] = (yield Promise.all([
                this.api.query.loans.rewardSupplySpeed(underlyingCurrencyId),
                this.api.query.loans.rewardBorrowSpeed(underlyingCurrencyId),
            ]))
                .map((rewardSpeedRaw) => Big(rewardSpeedRaw.toString()))
                .map((rewardSpeed) => calculateAnnualizedRewardAmount(rewardSpeed, blockTimeMs));
            // @note could be refactored to compute APR in lib if we can get underlyingCurrency/rewardCurrency exchange rate,
            // but is it safe to assume that exchange rate for btc/underlyingCurrency will be
            // always fed to the oracle and available?
            // Return rate per 1 UNIT of underlying currency and compute APR
            // on UI where all exchange rates are available.
            const lendRewardPerUnit = totalLiquidity.eq(0) ? lendRewardPerPool : lendRewardPerPool.div(totalLiquidity);
            const borrowRewardPerUnit = totalBorrows.eq(0) ? borrowRewardPerPool : borrowRewardPerPool.div(totalBorrows);
            return [lendRewardPerUnit, borrowRewardPerUnit];
        });
    }
    _getRewardCurrency() {
        return __awaiter(this, void 0, void 0, function* () {
            const rewardCurrencyId = this.api.consts.loans.rewardAssetId;
            return currencyIdToMonetaryCurrency(this.api, rewardCurrencyId);
        });
    }
    _getSubsidyReward(amount, rewardCurrency) {
        if (amount.eq(0)) {
            return null;
        }
        // Assumes native currency of parachain is always reward currency.
        return newMonetaryAmount(amount, rewardCurrency);
    }
    _getLoanAsset(underlyingCurrencyId, marketData) {
        return __awaiter(this, void 0, void 0, function* () {
            const underlyingCurrency = yield currencyIdToMonetaryCurrency(this.api, underlyingCurrencyId);
            const [lendApy, borrowApy, [totalLiquidity, availableCapacity, totalBorrows], rewardCurrency, exchangeRate] = yield Promise.all([
                this._getLendApy(underlyingCurrencyId),
                this._getBorrowApy(underlyingCurrencyId),
                this._getTotalLiquidityCapacityAndBorrows(underlyingCurrency, underlyingCurrencyId),
                this._getRewardCurrency(),
                this.oracleAPI.getExchangeRate(underlyingCurrency),
            ]);
            // Format data.
            const liquidationThreshold = decodePermill(marketData.liquidationThreshold);
            const collateralThreshold = decodePermill(marketData.collateralFactor);
            const supplyCap = newMonetaryAmount(marketData.supplyCap.toString(), underlyingCurrency);
            const borrowCap = newMonetaryAmount(marketData.borrowCap.toString(), underlyingCurrency);
            const [lendRewardAmountYearly, borrowRewardAmountYearly] = yield this._getLendAndBorrowYearlyRewardAmount(underlyingCurrencyId, totalLiquidity.toBig(), totalBorrows.toBig());
            const lendReward = this._getSubsidyReward(lendRewardAmountYearly, rewardCurrency);
            const borrowReward = this._getSubsidyReward(borrowRewardAmountYearly, rewardCurrency);
            return [
                underlyingCurrency,
                {
                    isActive: marketData.state.isActive,
                    lendApy,
                    borrowApy,
                    currency: underlyingCurrency,
                    totalLiquidity,
                    availableCapacity,
                    totalBorrows,
                    liquidationThreshold,
                    collateralThreshold,
                    lendReward,
                    borrowReward,
                    supplyCap,
                    borrowCap,
                    exchangeRate,
                },
            ];
        });
    }
    getLoanAssets() {
        return __awaiter(this, void 0, void 0, function* () {
            const marketsEntries = yield this.getLoansMarkets();
            const loanAssetsArray = yield Promise.all(marketsEntries.map(([currency, loansMarket]) => this._getLoanAsset(newCurrencyId(this.api, currency), loansMarket)));
            const loanAssets = loanAssetsArray.reduce((result, [currency, loanAsset]) => (Object.assign(Object.assign({}, result), { [currency.ticker]: loanAsset })), {});
            return loanAssets;
        });
    }
    _getLatestSupplyIndex(underlyingCurrencyId, lendTokenId, currentBlockNumber) {
        return __awaiter(this, void 0, void 0, function* () {
            const [marketSupplyState, marketSupplySpeed, totalIssuance] = yield Promise.all([
                this.api.query.loans.rewardSupplyState(underlyingCurrencyId),
                // Total KINT / INTR tokens awarded per block to suppliers of this market
                this.api.query.loans.rewardSupplySpeed(underlyingCurrencyId),
                this.api.query.tokens.totalIssuance(lendTokenId),
            ]);
            const lastIndex = Big(marketSupplyState.index.toString());
            const supplyRewardSpeed = Big(marketSupplySpeed.toString());
            const totalSupply = Big(totalIssuance.toString());
            if (totalSupply.eq(0)) {
                return Big(0);
            }
            const deltaBlocks = currentBlockNumber - marketSupplyState.block.toNumber();
            // NOTE: index needs to be multiplied by index scaling factor to have same amount of decimals
            //       as index computed on runtime side
            const deltaIndex = supplyRewardSpeed.mul(LOANS_REWARD_INDEX_SCALING_FACTOR).mul(deltaBlocks).div(totalSupply);
            return lastIndex.add(deltaIndex);
        });
    }
    _getAccruedSupplyReward(accountId, underlyingCurrencyId, lendTokenId, rewardCurrency, currentBlock) {
        return __awaiter(this, void 0, void 0, function* () {
            const [latestSupplyIndex, rewardSupplierIndex, lendTokenRawBalance] = yield Promise.all([
                this._getLatestSupplyIndex(underlyingCurrencyId, lendTokenId, currentBlock),
                this.api.query.loans.rewardSupplierIndex(underlyingCurrencyId, accountId),
                this.api.query.tokens.accounts(accountId, lendTokenId),
            ]);
            const supplierIndex = Big(rewardSupplierIndex.toString());
            const lendTokenBalance = Big(lendTokenRawBalance.free.toString()).add(lendTokenRawBalance.reserved.toString());
            const deltaIndex = latestSupplyIndex.sub(supplierIndex);
            const accruedRewardInPlanck = deltaIndex.mul(lendTokenBalance).div(LOANS_REWARD_INDEX_SCALING_FACTOR);
            const accruedSupplyReward = newMonetaryAmount(accruedRewardInPlanck, rewardCurrency);
            return accruedSupplyReward;
        });
    }
    _getLatestBorrowIndex(underlyingCurrencyId, currentBlockNumber) {
        return __awaiter(this, void 0, void 0, function* () {
            const [marketBorrowState, marketBorrowSpeed, totalBorrowsRaw] = yield Promise.all([
                this.api.query.loans.rewardBorrowState(underlyingCurrencyId),
                // Total KINT / INTR tokens awarded per block to suppliers of this market
                this.api.query.loans.rewardBorrowSpeed(underlyingCurrencyId),
                this.api.query.loans.totalBorrows(underlyingCurrencyId),
            ]);
            const lastBorrowIndex = Big(marketBorrowState.index.toString());
            const borrowRewardSpeed = Big(marketBorrowSpeed.toString());
            const totalBorrowed = Big(totalBorrowsRaw.toString());
            if (totalBorrowed.eq(0)) {
                return Big(0);
            }
            const deltaBlocks = currentBlockNumber - marketBorrowState.block.toNumber();
            // NOTE: index needs to be multiplied by index scaling factor to have same amount of decimals
            //       as index computed on runtime side
            const deltaIndex = borrowRewardSpeed.mul(LOANS_REWARD_INDEX_SCALING_FACTOR).mul(deltaBlocks).div(totalBorrowed);
            return lastBorrowIndex.add(deltaIndex);
        });
    }
    _getAccruedBorrowReward(accountId, underlyingCurrencyId, rewardCurrency, currentBlock) {
        return __awaiter(this, void 0, void 0, function* () {
            const [latestBorrowIndex, rewardBorrowerIndex, accountBorrowSnapshot] = yield Promise.all([
                this._getLatestBorrowIndex(underlyingCurrencyId, currentBlock),
                this.api.query.loans.rewardBorrowerIndex(underlyingCurrencyId, accountId),
                this.api.query.loans.accountBorrows(underlyingCurrencyId, accountId),
            ]);
            const borrowedAmount = Big(accountBorrowSnapshot.principal.toString());
            const borrowerIndex = Big(rewardBorrowerIndex.toString());
            const deltaIndex = latestBorrowIndex.sub(borrowerIndex);
            const accruedRewardInPlanck = deltaIndex.mul(borrowedAmount).div(LOANS_REWARD_INDEX_SCALING_FACTOR);
            const accruedBorrowReward = newMonetaryAmount(accruedRewardInPlanck, rewardCurrency);
            return accruedBorrowReward;
        });
    }
    getAccruedRewardsOfAccount(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            const [rewardAccrued, rewardCurrency, markets, blockNumber] = yield Promise.all([
                this.api.query.loans.rewardAccrued(accountId),
                this._getRewardCurrency(),
                this.getLoansMarkets(),
                this.api.query.system.number(),
            ]);
            const totalAccrued = newMonetaryAmount(rewardAccrued.toString(), rewardCurrency);
            const currentBlock = blockNumber.toNumber();
            let totalRewards = totalAccrued;
            const rewardsPerMarket = {};
            for (const [underlyingCurrency, market] of markets) {
                // the following computes the reward not claimed and not yet computed, for a single market
                const underlyingCurrencyId = newCurrencyId(this.api, underlyingCurrency);
                const [lendReward, borrowReward] = yield Promise.all([
                    this._getAccruedSupplyReward(accountId, underlyingCurrencyId, market.lendTokenId, rewardCurrency, currentBlock),
                    this._getAccruedBorrowReward(accountId, underlyingCurrencyId, rewardCurrency, currentBlock),
                ]);
                rewardsPerMarket[underlyingCurrency.ticker] = {
                    lend: lendReward.toBig().gt(0) ? lendReward : null,
                    borrow: borrowReward.toBig().gt(0) ? borrowReward : null,
                };
                totalRewards = totalRewards.add(lendReward).add(borrowReward);
            }
            return { total: totalRewards, perMarket: rewardsPerMarket };
        });
    }
    // Check that market for given currency is added and in active state.
    _checkMarketState(currency, action) {
        return __awaiter(this, void 0, void 0, function* () {
            const underlyingCurrencyId = newCurrencyId(this.api, currency);
            const underlyingCurrencyMarket = yield this.api.query.loans.markets(underlyingCurrencyId);
            if (underlyingCurrencyMarket.isNone) {
                throw new Error(`Cannot ${action} currency that does not have market created`);
            }
            if (underlyingCurrencyMarket.unwrap().state.isPending) {
                throw new Error(`Cannot ${action} currency that does not have active market.`);
            }
        });
    }
    lend(underlyingCurrency, amount) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._checkMarketState(underlyingCurrency, "lend");
            const underlyingCurrencyId = newCurrencyId(this.api, underlyingCurrency);
            const lendExtrinsic = this.api.tx.loans.mint(underlyingCurrencyId, amount.toString(true));
            return { extrinsic: lendExtrinsic, event: this.api.events.loans.Deposited };
        });
    }
    withdraw(underlyingCurrency, amount) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._checkMarketState(underlyingCurrency, "withdraw");
            const underlyingCurrencyId = newCurrencyId(this.api, underlyingCurrency);
            const withdrawExtrinsic = this.api.tx.loans.redeem(underlyingCurrencyId, amount.toString(true));
            return { extrinsic: withdrawExtrinsic, event: this.api.events.loans.Redeemed };
        });
    }
    withdrawAll(underlyingCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._checkMarketState(underlyingCurrency, "withdraw");
            const underlyingCurrencyId = newCurrencyId(this.api, underlyingCurrency);
            const withdrawExtrinsic = this.api.tx.loans.redeemAll(underlyingCurrencyId);
            return { extrinsic: withdrawExtrinsic, event: this.api.events.loans.Redeemed };
        });
    }
    enableAsCollateral(underlyingCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._checkMarketState(underlyingCurrency, "enable collateral");
            const underlyingCurrencyId = newCurrencyId(this.api, underlyingCurrency);
            const enableCollateralExtrinsic = this.api.tx.loans.depositAllCollateral(underlyingCurrencyId);
            return { extrinsic: enableCollateralExtrinsic, event: this.api.events.loans.DepositCollateral };
        });
    }
    disableAsCollateral(underlyingCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._checkMarketState(underlyingCurrency, "disable collateral");
            const underlyingCurrencyId = newCurrencyId(this.api, underlyingCurrency);
            const enableCollateralExtrinsic = this.api.tx.loans.withdrawAllCollateral(underlyingCurrencyId);
            return { extrinsic: enableCollateralExtrinsic, event: this.api.events.loans.WithdrawCollateral };
        });
    }
    claimAllSubsidyRewards() {
        const claimRewardsExtrinsic = this.api.tx.loans.claimReward();
        return { extrinsic: claimRewardsExtrinsic, event: this.api.events.loans.RewardPaid };
    }
    borrow(underlyingCurrency, amount) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._checkMarketState(underlyingCurrency, "borrow");
            const underlyingCurrencyId = newCurrencyId(this.api, underlyingCurrency);
            const borrowExtrinsic = this.api.tx.loans.borrow(underlyingCurrencyId, amount.toString(true));
            return { extrinsic: borrowExtrinsic, event: this.api.events.loans.Borrowed };
        });
    }
    repay(underlyingCurrency, amount) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._checkMarketState(underlyingCurrency, "repay debt of");
            const underlyingCurrencyId = newCurrencyId(this.api, underlyingCurrency);
            const repayExtrinsic = this.api.tx.loans.repayBorrow(underlyingCurrencyId, amount.toString(true));
            return { extrinsic: repayExtrinsic, event: this.api.events.loans.RepaidBorrow };
        });
    }
    repayAll(underlyingCurrency) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this._checkMarketState(underlyingCurrency, "repay all debt of");
            const underlyingCurrencyId = newCurrencyId(this.api, underlyingCurrency);
            const repayAllExtrinsic = this.api.tx.loans.repayBorrowAll(underlyingCurrencyId);
            return { extrinsic: repayAllExtrinsic, event: this.api.events.loans.RepaidBorrow };
        });
    }
    liquidateBorrowPosition(borrower, liquidationCurrency, repayAmount, collateralCurrency) {
        const liquidationCurrencyId = newCurrencyId(this.api, liquidationCurrency);
        const collateralCurrencyId = newCurrencyId(this.api, collateralCurrency);
        const rawAmount = repayAmount.toString(true);
        const liquidateBorrowExtrinsic = this.api.tx.loans.liquidateBorrow(borrower, liquidationCurrencyId, rawAmount, collateralCurrencyId);
        return { extrinsic: liquidateBorrowExtrinsic, event: this.api.events.loans.LiquidatedBorrow };
    }
}
//# sourceMappingURL=loans.js.map